import { useDispatch, useSelector } from 'react-redux';
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import type { TableCell } from '@worldathletics/types';

import { BlockMenu } from '@/components/atoms';
import { EditableCell } from '@/components/atoms/editable-cell';
import { useSelectionToolbarApi } from '@/context/selection-toolbar/selection-toolbar-context';
import { expandTableGrid } from '@/lib/expand-table-grid';
import { sanitizeHtml } from '@/lib/edit-text-serialize';
import {
  registerEditorFlush,
  setEditorFlushDirty,
} from '@/lib/editor-flush-registry';
import { type CellSelection } from '@/lib/table-ops';
import type { RootState } from '@/context/store/store';
import {
  buildColgroupStyles,
  getColumnAlign,
  getColumnWidthStyle,
  tableHasExplicitColumnWidths,
} from '@worldathletics/utilities';
import {
  moveNode,
  updateNode,
  type DocumentEntity,
} from '@/context/store/slices/documentDataSlice';
import {
  setAddBlockIntent,
  setNewBlockModal,
  setDeleteBlockConfirmationModal,
} from '@/context/store/slices/siteSlice';
import { useSiblingPosition } from '@/hooks/use-sibling-position';
import { useReportActiveBlock } from '@/hooks/use-report-active-block';

import { ColumnPopover } from './column-popover';
import { TableToolbar } from './table-toolbar';
import './document-editor-table.scss';

type FocusedCell = { r: number; c: number } | null;
type TableNode = DocumentEntity & { type: 'table' };
type Props = { node: TableNode };
type CellStyle = { textAlign?: 'left' | 'center' | 'right'; width?: string };

const noop = (): void => undefined;
const DEBOUNCE_MS = 300;

function cellKey(r: number, c: number): string {
  return `${String(r)}:${String(c)}`;
}

function parseCellKey(key: string): { r: number; c: number } | null {
  const [rawR, rawC] = key.split(':');
  const r = Number(rawR);
  const c = Number(rawC);
  if (Number.isNaN(r) || Number.isNaN(c)) return null;
  return { r, c };
}

type IdleHandle = { cancel: () => void };

function scheduleAfterIdle(cb: () => void, timeoutMs: number): IdleHandle {
  const win = globalThis as typeof globalThis & {
    requestIdleCallback?: (
      callback: IdleRequestCallback,
      options?: IdleRequestOptions,
    ) => number;
    cancelIdleCallback?: (handle: number) => void;
  };
  if (typeof win.requestIdleCallback === 'function') {
    const id = win.requestIdleCallback(() => cb(), { timeout: timeoutMs });
    return {
      cancel: () => {
        win.cancelIdleCallback?.(id);
      },
    };
  }
  const id = globalThis.setTimeout(cb, timeoutMs);
  return {
    cancel: () => {
      globalThis.clearTimeout(id);
    },
  };
}

/**
 * Overlay sparse raw cell HTML onto a base grid, sanitizing once per dirty
 * cell. Clones only the rows array and each touched row/cell.
 */
function materializeRows(
  baseRows: TableCell[][],
  pending: Map<string, string>,
): { rows: TableCell[][]; changed: boolean } {
  if (pending.size === 0) return { rows: baseRows, changed: false };

  let nextRows = baseRows;
  let clonedRows = false;
  let changed = false;

  for (const [key, rawHtml] of pending) {
    const coords = parseCellKey(key);
    if (!coords) continue;
    const { r, c } = coords;
    const row = nextRows[r];
    if (!row) continue;
    const cell = row[c];
    if (!cell) continue;
    const sanitized = sanitizeHtml(rawHtml);
    if (cell.text === sanitized) continue;
    changed = true;
    if (!clonedRows) {
      nextRows = nextRows.slice();
      clonedRows = true;
    }
    const nextRow = row.slice();
    nextRow[c] = { ...cell, text: sanitized };
    nextRows[r] = nextRow;
  }

  return { rows: nextRows, changed };
}

type MemoCellProps = {
  r: number;
  c: number;
  text: string;
  rowSpan: number;
  colSpan: number;
  selected: boolean;
  style?: CellStyle;
  onCellInput: (cell: HTMLTableCellElement) => void;
  onCompositionChange: (composing: boolean) => void;
  onFocus: (e: FocusEvent<HTMLElement>) => void;
  onMouseDown: (e: ReactMouseEvent<HTMLElement>) => void;
  onMouseEnter: (e: ReactMouseEvent<HTMLElement>) => void;
  onMouseUp: () => void;
};

const MemoTableCell = memo(function MemoTableCell({
  r,
  c,
  text,
  rowSpan,
  colSpan,
  selected,
  style,
  onCellInput,
  onCompositionChange,
  onFocus,
  onMouseDown,
  onMouseEnter,
  onMouseUp,
}: MemoCellProps) {
  return (
    <EditableCell
      data-cell-r={r}
      data-cell-c={c}
      contentEditable
      suppressContentEditableWarning
      rowSpan={rowSpan}
      colSpan={colSpan}
      style={style}
      className={selected ? 'is-selected' : undefined}
      onFocus={onFocus}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onMouseUp={onMouseUp}
      text={text}
      onCellInput={onCellInput}
      onCompositionChange={onCompositionChange}
    />
  );
});

const DocumentEditorTableComponent = function DocumentEditorTable({
  node,
}: Props) {
  const bookId = useSelector(
    (state: RootState) => state.documentData.sourceNodeKey?.split(':')[0],
  );
  const nodeId = useSelector(
    (state: RootState) => state.documentData.sourceNodeKey?.split(':')[1],
  );
  const dispatch = useDispatch();
  const { registerSelectionHost } = useSelectionToolbarApi();
  const tableRef = useRef<HTMLTableElement | null>(null);
  const tableRootRef = useRef<HTMLDivElement | null>(null);
  const position = useSiblingPosition(node.id);
  const reportActive = useReportActiveBlock(node);
  const reportActiveRef = useRef(reportActive);
  useEffect(() => {
    reportActiveRef.current = reportActive;
  }, [reportActive]);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [focusedCell, setFocusedCell] = useState<FocusedCell>(null);
  const [selection, setSelection] = useState<CellSelection | null>(null);
  const [popoverColumn, setPopoverColumn] = useState<number | null>(null);

  // Base grid last known from Redux / structural ops. Typing overlays live in
  // `pendingCellHtmlRef` as raw HTML until flush materializes + sanitizes.
  const baseRowsRef = useRef(node.rows);
  const nodeRef = useRef(node);
  const pendingCellHtmlRef = useRef(new Map<string, string>());
  const dirtyRef = useRef(false);
  const composingRef = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleHandleRef = useRef<IdleHandle | null>(null);
  const dragStartRef = useRef<{ r: number; c: number } | null>(null);

  useEffect(() => {
    nodeRef.current = node;
    if (!dirtyRef.current) {
      baseRowsRef.current = node.rows;
    }
  }, [node]);

  const cancelScheduledFlush = useCallback((): void => {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (idleHandleRef.current !== null) {
      idleHandleRef.current.cancel();
      idleHandleRef.current = null;
    }
  }, []);

  const getWorkingNode = useCallback((): TableNode => {
    const { rows } = materializeRows(
      baseRowsRef.current,
      pendingCellHtmlRef.current,
    );
    return { ...nodeRef.current, rows };
  }, []);

  /**
   * Materialize sparse drafts, hand off dirty ownership to Redux first when
   * content changed, then clear the registry flag — never emit a transient
   * clean state between the two.
   */
  const flushDraftToRedux = useCallback((): void => {
    cancelScheduledFlush();
    if (!dirtyRef.current && pendingCellHtmlRef.current.size === 0) {
      setEditorFlushDirty(node.id, false);
      return;
    }

    const { rows, changed } = materializeRows(
      baseRowsRef.current,
      pendingCellHtmlRef.current,
    );
    pendingCellHtmlRef.current.clear();
    baseRowsRef.current = rows;

    if (changed) {
      // Redux becomes dirty first so Save/Discard stay visible.
      dispatch(
        updateNode({
          id: node.id,
          changes: { rows },
        }),
      );
    }

    dirtyRef.current = false;
    setEditorFlushDirty(node.id, false);
  }, [cancelScheduledFlush, dispatch, node.id]);

  const scheduleDraftFlush = useCallback((): void => {
    if (!dirtyRef.current) {
      dirtyRef.current = true;
      setEditorFlushDirty(node.id, true);
    }
    if (composingRef.current) return;

    cancelScheduledFlush();
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      idleHandleRef.current = scheduleAfterIdle(() => {
        idleHandleRef.current = null;
        flushDraftToRedux();
      }, DEBOUNCE_MS);
    }, DEBOUNCE_MS);
  }, [cancelScheduledFlush, flushDraftToRedux, node.id]);

  useEffect(() => {
    return registerEditorFlush(node.id, {
      flush: async () => {
        flushDraftToRedux();
      },
      cancel: () => {
        cancelScheduledFlush();
        dirtyRef.current = false;
        pendingCellHtmlRef.current.clear();
        baseRowsRef.current = nodeRef.current.rows;
        setEditorFlushDirty(node.id, false);
      },
      isBusy: () =>
        dirtyRef.current ||
        debounceTimerRef.current !== null ||
        idleHandleRef.current !== null ||
        pendingCellHtmlRef.current.size > 0,
    });
  }, [cancelScheduledFlush, flushDraftToRedux, node.id]);

  useEffect(() => {
    const root = tableRootRef.current;
    if (!root) return;
    return registerSelectionHost({
      id: node.id,
      element: root,
    });
  }, [node.id, registerSelectionHost]);

  // Flush when leaving the table for another block. Moving focus within the
  // table (caption/toolbar) keeps the draft; the next structural action or
  // Save/Tab will materialize it.
  useEffect(() => {
    const tableRootEl = tableRootRef.current;
    if (tableRootEl === null) return;
    const tableRoot: HTMLDivElement = tableRootEl;
    function handleFocusOut(e: globalThis.FocusEvent): void {
      const next = e.relatedTarget as Node | null;
      if (next && tableRoot.contains(next)) return;
      flushDraftToRedux();
    }
    tableRoot.addEventListener('focusout', handleFocusOut);
    return () => {
      tableRoot.removeEventListener('focusout', handleFocusOut);
      flushDraftToRedux();
    };
  }, [flushDraftToRedux]);

  const grid = useMemo(
    () => expandTableGrid(node.rows, node.columns?.length),
    [node.rows, node.columns],
  );

  const totalColumns = grid[0]?.length ?? 0;

  const columnStyles = useMemo((): Array<CellStyle | undefined> => {
    return Array.from({ length: totalColumns }, (_, c) => {
      const align = getColumnAlign(node.columns, c);
      const colWidth = getColumnWidthStyle(node.columns, c);
      const style: CellStyle = {
        ...(align !== undefined ? { textAlign: align } : {}),
        ...(colWidth?.endsWith('px') ? { width: colWidth } : {}),
      };
      return Object.keys(style).length > 0 ? style : undefined;
    });
  }, [node.columns, totalColumns]);

  const focusedCellSpan = useMemo(() => {
    if (!focusedCell) return null;
    const gc = grid[focusedCell.r]?.[focusedCell.c];
    if (!gc?.isAnchor) return null;
    return {
      rowSpan: gc.cell.rowSpan ?? 1,
      colSpan: gc.cell.colSpan ?? 1,
    };
  }, [focusedCell, grid]);

  const handleCellInput = useCallback(
    (cell: HTMLTableCellElement): void => {
      const rawR = cell.dataset.cellR;
      const rawC = cell.dataset.cellC;
      if (rawR === undefined || rawC === undefined) return;
      const r = Number(rawR);
      const c = Number(rawC);
      if (Number.isNaN(r) || Number.isNaN(c)) return;
      // Constant-time hot path: record raw HTML only. Sanitize on flush.
      pendingCellHtmlRef.current.set(cellKey(r, c), cell.innerHTML);
      scheduleDraftFlush();
    },
    [scheduleDraftFlush],
  );

  const handleCompositionChange = useCallback((composing: boolean): void => {
    composingRef.current = composing;
  }, []);

  const applyStructural = useCallback(
    (next: TableNode): void => {
      // Structural ops already receive materializeRows via getWorkingNode().
      cancelScheduledFlush();
      pendingCellHtmlRef.current.clear();
      baseRowsRef.current = next.rows;
      dirtyRef.current = false;

      dispatch(
        updateNode({
          id: node.id,
          changes: {
            rows: next.rows,
            caption: next.caption,
            headerRowCount: next.headerRowCount,
            rowHeaderCol: next.rowHeaderCol,
            columns: next.columns,
          },
        }),
      );
      setEditorFlushDirty(node.id, false);
    },
    [cancelScheduledFlush, dispatch, node.id],
  );

  function focusCell(r: number, c: number): void {
    const cell = tableRef.current?.querySelector<HTMLElement>(
      `[data-cell-r="${String(r)}"][data-cell-c="${String(c)}"]`,
    );
    cell?.focus();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTableElement>): void {
    const target = e.target as HTMLElement;
    const rawR = target.dataset.cellR;
    const rawC = target.dataset.cellC;
    if (rawR === undefined || rawC === undefined) return;
    const r = Number(rawR);
    const c = Number(rawC);
    if (Number.isNaN(r) || Number.isNaN(c)) return;
    const totalCols = grid[0]?.length ?? 0;

    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      flushDraftToRedux();
      if (c + 1 < totalCols) {
        focusCell(r, c + 1);
      } else if (r + 1 < grid.length) {
        focusCell(r + 1, 0);
      }
    } else if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      flushDraftToRedux();
      if (c > 0) {
        focusCell(r, c - 1);
      } else if (r > 0) {
        focusCell(r - 1, totalCols - 1);
      }
    }
  }

  const handleCellFocus = useCallback(
    (e: FocusEvent<HTMLElement>): void => {
      const rawR = e.currentTarget.dataset.cellR;
      const rawC = e.currentTarget.dataset.cellC;
      if (rawR === undefined || rawC === undefined) return;
      const r = Number(rawR);
      const c = Number(rawC);
      if (Number.isNaN(r) || Number.isNaN(c)) return;
      setFocusedCell({ r, c });
      reportActiveRef.current();
    },
    [],
  );

  const onCellMouseDown = useCallback(
    (e: ReactMouseEvent<HTMLElement>): void => {
      const rawR = e.currentTarget.dataset.cellR;
      const rawC = e.currentTarget.dataset.cellC;
      if (rawR === undefined || rawC === undefined) return;
      const r = Number(rawR);
      const c = Number(rawC);
      if (Number.isNaN(r) || Number.isNaN(c)) return;
      dragStartRef.current = { r, c };
      setSelection({ r1: r, c1: c, r2: r, c2: c });
    },
    [],
  );

  const onCellMouseEnter = useCallback(
    (e: ReactMouseEvent<HTMLElement>): void => {
      const start = dragStartRef.current;
      if (!start) return;
      const rawR = e.currentTarget.dataset.cellR;
      const rawC = e.currentTarget.dataset.cellC;
      if (rawR === undefined || rawC === undefined) return;
      const r = Number(rawR);
      const c = Number(rawC);
      if (Number.isNaN(r) || Number.isNaN(c)) return;
      setSelection({ r1: start.r, c1: start.c, r2: r, c2: c });
    },
    [],
  );

  const onCellMouseUp = useCallback((): void => {
    dragStartRef.current = null;
  }, []);

  const selectionBounds = useMemo(() => {
    if (!selection) return null;
    return {
      r1: Math.min(selection.r1, selection.r2),
      r2: Math.max(selection.r1, selection.r2),
      c1: Math.min(selection.c1, selection.c2),
      c2: Math.max(selection.c1, selection.c2),
    };
  }, [selection]);

  function handleDelete(): void {
    flushDraftToRedux();
    dispatch(
      setDeleteBlockConfirmationModal({
        open: true,
        data: {
          bookId: bookId ?? '',
          nodeId: nodeId ?? '',
          blockId: node.id,
          blockType: 'table',
        },
      }),
    );
  }

  function handleAddAbove(): void {
    flushDraftToRedux();
    dispatch(setAddBlockIntent({ mode: 'sibling-before', targetId: node.id }));
    dispatch(setNewBlockModal(true));
  }

  function handleAddBelow(): void {
    flushDraftToRedux();
    dispatch(setAddBlockIntent({ mode: 'sibling-after', targetId: node.id }));
    dispatch(setNewBlockModal(true));
  }

  function handleMoveUp(): void {
    if (!position.canMoveUp) return;
    flushDraftToRedux();
    dispatch(
      moveNode({
        id: node.id,
        newParentId: position.parentId,
        newIndex: position.siblingIndex - 1,
      }),
    );
  }

  function handleMoveDown(): void {
    if (!position.canMoveDown) return;
    flushDraftToRedux();
    dispatch(
      moveNode({
        id: node.id,
        newParentId: position.parentId,
        newIndex: position.siblingIndex + 1,
      }),
    );
  }

  const colgroupStyles = buildColgroupStyles(node.columns, totalColumns);
  const useFixedLayout = tableHasExplicitColumnWidths(
    node.columns,
    totalColumns,
  );
  const tableMinWidthPx = colgroupStyles.reduce((sum, col) => {
    const width = col.width;
    if (width?.endsWith('px')) {
      return sum + parseFloat(width);
    }
    return sum;
  }, 0);

  const tableRows = grid.length;
  const tableCols = totalColumns;
  const isSplittable =
    focusedCellSpan !== null &&
    (focusedCellSpan.rowSpan > 1 || focusedCellSpan.colSpan > 1);

  return (
    <div
      ref={tableRootRef}
      data-type="card"
      className={`doc-editor-table ${isMenuOpen ? 'is-menu-open' : ''}`}
    >
      <button
        type="button"
        className="doc-editor-table__move-btn is-up"
        onClick={handleMoveUp}
        disabled={!position.canMoveUp}
        aria-label="Move up"
        title="Move up"
      >
        <ChevronUpIcon size={16} />
      </button>
      <button
        type="button"
        className="doc-editor-table__move-btn is-down"
        onClick={handleMoveDown}
        disabled={!position.canMoveDown}
        aria-label="Move down"
        title="Move down"
      >
        <ChevronDownIcon size={16} />
      </button>

      <div className="doc-editor-table__toolbar">
        <BlockMenu
          variant="leaf"
          numberingStyle="free"
          onNumberingStyleChange={noop}
          propagateNumbering={true}
          onPropagateNumberingChange={noop}
          onDelete={handleDelete}
          onAddAbove={handleAddAbove}
          onAddBelow={handleAddBelow}
          onMoveUp={handleMoveUp}
          onMoveDown={handleMoveDown}
          canMoveUp={position.canMoveUp}
          canMoveDown={position.canMoveDown}
          onOpenChange={setIsMenuOpen}
        />
      </div>
      <TableToolbar
        node={node}
        getWorkingNode={getWorkingNode}
        apply={applyStructural}
        focusedCell={focusedCell}
        selection={selection}
        tableRows={tableRows}
        tableCols={tableCols}
        isSplittable={isSplittable}
      />
      {popoverColumn !== null && (
        <ColumnPopover
          key={popoverColumn}
          node={node}
          columnIndex={popoverColumn}
          onClose={() => setPopoverColumn(null)}
        />
      )}
      <table
        ref={tableRef}
        className={`doc-editor-table__grid${useFixedLayout ? ' has-fixed-layout' : ''}`}
        style={
          tableMinWidthPx > 0
            ? { minWidth: `${String(tableMinWidthPx)}px` }
            : undefined
        }
        onKeyDown={handleKeyDown}
      >
        {totalColumns > 0 && (
          <colgroup>
            {colgroupStyles.map((col, i) => (
              <col
                key={i}
                style={{
                  ...(col.width !== undefined ? { width: col.width } : {}),
                }}
              />
            ))}
          </colgroup>
        )}
        {totalColumns > 0 && (
          <thead>
            <tr className="doc-editor-table__column-header-row">
              {Array.from({ length: totalColumns }).map((_, i) => (
                <th key={i}>
                  <button
                    type="button"
                    aria-label={`Column ${String(i + 1)}`}
                    onClick={() => setPopoverColumn(i)}
                  >
                    ☰
                  </button>
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {grid.map((row, r) => (
            <tr key={String(r)}>
              {row
                .filter(gc => gc.isAnchor)
                .map(gc => {
                  const selected =
                    selectionBounds !== null &&
                    gc.r >= selectionBounds.r1 &&
                    gc.r <= selectionBounds.r2 &&
                    gc.c >= selectionBounds.c1 &&
                    gc.c <= selectionBounds.c2;
                  return (
                    <MemoTableCell
                      key={`${String(gc.r)}-${String(gc.c)}`}
                      r={gc.r}
                      c={gc.c}
                      text={gc.cell.text}
                      rowSpan={gc.cell.rowSpan ?? 1}
                      colSpan={gc.cell.colSpan ?? 1}
                      selected={selected}
                      style={columnStyles[gc.c]}
                      onCellInput={handleCellInput}
                      onCompositionChange={handleCompositionChange}
                      onFocus={handleCellFocus}
                      onMouseDown={onCellMouseDown}
                      onMouseEnter={onCellMouseEnter}
                      onMouseUp={onCellMouseUp}
                    />
                  );
                })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DocumentEditorTable = memo(DocumentEditorTableComponent);

export { DocumentEditorTable };
